import { useCallback, useEffect, useRef, useState } from "react";

const POLYGON_OPTIONS = {
  fillColor: "#ee4f4f",
  fillOpacity: 0.5,
  strokeWeight: 2,
  strokeColor: "#ee4f4f",
  clickable: false,
  editable: false,
  zIndex: 1,
};

const DRAFT_LINE_OPTIONS = {
  strokeColor: "#ee4f4f",
  strokeOpacity: 0.8,
  strokeWeight: 2,
  clickable: false,
  zIndex: 2,
};

/** Max meters from the first vertex to treat a click as "close polygon". */
const CLOSE_VERTEX_METERS = 25;

/**
 * Replaces google.maps.drawing.DrawingManager with custom click-to-draw polygon.
 *
 * @param {object} params
 * @param {google.maps.Map | null} params.map
 * @param {{ lat: number, lng: number }[] | null} params.polygonCoords
 * @param {(coords: { lat: number, lng: number }[] | null) => void} params.onPolygonSet
 * @returns {{
 *   polygon: google.maps.Polygon | null,
 *   isDrawing: boolean,
 *   draftVertexCount: number,
 *   startDrawing: () => void,
 *   finishDrawing: () => void,
 *   cancelDrawing: () => void,
 *   reset: () => void,
 * }}
 */
export function useGeoFencePolygonDraw({ map, polygonCoords, onPolygonSet }) {
  const [polygon, setPolygon] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [draftVertexCount, setDraftVertexCount] = useState(0);

  const verticesRef = useRef([]);
  const draftPolylineRef = useRef(null);
  const previewLineRef = useRef(null);
  const vertexMarkersRef = useRef([]);
  const listenersRef = useRef([]);
  const polygonRef = useRef(null);

  const clearDraftOverlays = useCallback(() => {
    if (draftPolylineRef.current) {
      draftPolylineRef.current.setMap(null);
      draftPolylineRef.current = null;
    }
    if (previewLineRef.current) {
      previewLineRef.current.setMap(null);
      previewLineRef.current = null;
    }
    vertexMarkersRef.current.forEach((marker) => marker.setMap(null));
    vertexMarkersRef.current = [];
    verticesRef.current = [];
    setDraftVertexCount(0);
  }, []);

  const removeListeners = useCallback(() => {
    listenersRef.current.forEach((listener) => {
      window.google.maps.event.removeListener(listener);
    });
    listenersRef.current = [];
  }, []);

  const getPolygonCoordinates = useCallback((_polygon) => {
    const coords = [];
    _polygon.getPaths().forEach((path) => {
      path.forEach((latLng) => {
        coords.push({ lat: latLng.lat(), lng: latLng.lng() });
      });
    });
    return coords;
  }, []);

  const distanceMeters = useCallback((a, b) => {
    if (window.google.maps.geometry?.spherical?.computeDistanceBetween) {
      return window.google.maps.geometry.spherical.computeDistanceBetween(a, b);
    }
    const toRad = (deg) => (deg * Math.PI) / 180;
    const R = 6371000;
    const dLat = toRad(b.lat() - a.lat());
    const dLng = toRad(b.lng() - a.lng());
    const lat1 = toRad(a.lat());
    const lat2 = toRad(b.lat());
    const h =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(h));
  }, []);

  const updateDraftPolyline = useCallback(() => {
    const path = verticesRef.current.map((v) => v);
    if (!draftPolylineRef.current) {
      draftPolylineRef.current = new window.google.maps.Polyline({
        ...DRAFT_LINE_OPTIONS,
        path,
        map,
      });
    } else {
      draftPolylineRef.current.setPath(path);
    }
  }, [map]);

  const addVertexMarker = useCallback(
    (latLng, isFirst) => {
      const marker = new window.google.maps.Marker({
        position: latLng,
        map,
        clickable: isFirst,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: isFirst ? 7 : 5,
          fillColor: "#ee4f4f",
          fillOpacity: 1,
          strokeColor: "#ffffff",
          strokeWeight: 2,
        },
        zIndex: 3,
      });
      vertexMarkersRef.current.push(marker);
      return marker;
    },
    [map]
  );

  const completePolygon = useCallback(() => {
    if (verticesRef.current.length < 3 || !map) return;

    removeListeners();
    const paths = verticesRef.current.map((v) => ({
      lat: v.lat(),
      lng: v.lng(),
    }));
    clearDraftOverlays();

    const _polygon = new window.google.maps.Polygon({
      paths,
      ...POLYGON_OPTIONS,
      map,
    });
    polygonRef.current = _polygon;
    setPolygon(_polygon);
    setIsDrawing(false);
    map.setOptions({ draggableCursor: null });
    onPolygonSet(getPolygonCoordinates(_polygon));
  }, [
    map,
    removeListeners,
    clearDraftOverlays,
    onPolygonSet,
    getPolygonCoordinates,
  ]);

  const addVertex = useCallback(
    (latLng) => {
      if (!map) return;

      const vertices = verticesRef.current;
      if (
        vertices.length >= 3 &&
        distanceMeters(vertices[0], latLng) <= CLOSE_VERTEX_METERS
      ) {
        completePolygon();
        return;
      }

      vertices.push(latLng);
      setDraftVertexCount(vertices.length);
      const marker = addVertexMarker(latLng, vertices.length === 1);
      if (vertices.length === 1) {
        const closeListener = marker.addListener("click", () => {
          if (verticesRef.current.length >= 3) {
            completePolygon();
          }
        });
        listenersRef.current.push(closeListener);
      }
      updateDraftPolyline();
    },
    [map, distanceMeters, completePolygon, addVertexMarker, updateDraftPolyline]
  );

  const startDrawing = useCallback(() => {
    if (!map || polygonRef.current) return;
    clearDraftOverlays();
    removeListeners();
    setIsDrawing(true);
    map.setOptions({ draggableCursor: "crosshair" });

    const clickListener = map.addListener("click", (e) => {
      if (e.latLng) addVertex(e.latLng);
    });

    const dblClickListener = map.addListener("dblclick", (e) => {
      e.stop();
      const vertices = verticesRef.current;
      // The second click of a dblclick often already added a duplicate vertex.
      if (vertices.length >= 2) {
        const last = vertices[vertices.length - 1];
        const prev = vertices[vertices.length - 2];
        if (distanceMeters(last, prev) <= CLOSE_VERTEX_METERS) {
          vertices.pop();
          const marker = vertexMarkersRef.current.pop();
          marker?.setMap(null);
          setDraftVertexCount(vertices.length);
          updateDraftPolyline();
        }
      }
      if (verticesRef.current.length >= 3) {
        completePolygon();
      }
    });

    const moveListener = map.addListener("mousemove", (e) => {
      if (!e.latLng || verticesRef.current.length === 0) return;
      const last = verticesRef.current[verticesRef.current.length - 1];
      const path = [last, e.latLng];
      if (!previewLineRef.current) {
        previewLineRef.current = new window.google.maps.Polyline({
          ...DRAFT_LINE_OPTIONS,
          strokeOpacity: 0.45,
          path,
          map,
        });
      } else {
        previewLineRef.current.setPath(path);
      }
    });

    listenersRef.current = [clickListener, dblClickListener, moveListener];
  }, [
    map,
    clearDraftOverlays,
    removeListeners,
    addVertex,
    completePolygon,
    distanceMeters,
    updateDraftPolyline,
  ]);

  const cancelDrawing = useCallback(() => {
    removeListeners();
    clearDraftOverlays();
    setIsDrawing(false);
    if (map) {
      map.setOptions({ draggableCursor: null });
    }
  }, [map, removeListeners, clearDraftOverlays]);

  const finishDrawing = useCallback(() => {
    completePolygon();
  }, [completePolygon]);

  const reset = useCallback(() => {
    cancelDrawing();
    if (polygonRef.current) {
      polygonRef.current.setMap(null);
      polygonRef.current = null;
    }
    setPolygon(null);
    onPolygonSet(null);
  }, [cancelDrawing, onPolygonSet]);

  // Render saved polygon when coords arrive and none exists yet.
  useEffect(() => {
    if (!map || !polygonCoords || polygonRef.current) return;

    const _polygon = new window.google.maps.Polygon({
      paths: polygonCoords,
      ...POLYGON_OPTIONS,
      map,
    });
    polygonRef.current = _polygon;
    setPolygon(_polygon);
  }, [map, polygonCoords]);

  // Cleanup on unmount / map change.
  useEffect(() => {
    return () => {
      removeListeners();
      clearDraftOverlays();
      if (polygonRef.current) {
        polygonRef.current.setMap(null);
        polygonRef.current = null;
      }
    };
  }, [removeListeners, clearDraftOverlays]);

  useEffect(() => {
    if (!isDrawing && map) {
      map.setOptions({ draggableCursor: null });
    }
  }, [isDrawing, map]);

  return {
    polygon,
    isDrawing,
    draftVertexCount,
    startDrawing,
    finishDrawing,
    cancelDrawing,
    reset,
  };
}

export { POLYGON_OPTIONS };
